/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Money } from '__generated__/types';
import { PartType } from 'collections/part';
import { getInitialValues } from 'components/Deal/OrderConfirmation/shared/ocFormHelpers';
import { FormLot, OCFormValues } from 'components/Deal/OrderConfirmation/shared/ocFormValues';
import Notification from 'components/UI/Notification';
import { dateFormat } from 'components/utils/formatters';
import { XOMETRY_VAT_ENABLED } from 'config/regions/features';
import { isEmpty, omit, sortBy } from 'es-toolkit/compat';
import { CommentRequest, OCLot, OrderConfirmation } from 'interfaces/stores/OrderConfirmation';
import { IAddress } from 'interfaces/stores/Shipping';
import { action, computed, observable } from 'mobx';
import { routesApi } from 'routes/api';
import { RootStore } from 'stores/RootStore';
import api, { camelizeKeys, decamelizeKeys, omsGet } from 'utils/axios';
import dayjs from 'utils/dayjs';
import { DealOrderConfirmationVatQuery } from 'utils/graphql/queries/__generated__/dealOrderConfirmationVat';
import { uniqID } from 'utils/uniqID';

interface CreditOverflow {
  creditLimit: number | Money;
  currentDebt: number | Money;
  currentOrder: number | Money;
  currentOpenOrders: number | Money;
  total: number | Money;
  org?: string;
  person?: string;
  ba?: string;
  requestedFields?: string[];
  errorMessage?: string;
}

class OrderConfirmationStore {
  public rootStore: RootStore;

  @observable isFetching = true;
  @observable dealId = 0;
  @observable isExist = false;
  @observable creditOverflow: CreditOverflow | undefined = undefined;
  @observable reloadOnUpdate = true;
  @observable orderConfirmation: OrderConfirmation = {} as OrderConfirmation;
  @observable automationJobCreating = false;
  @observable billingAccountCountry: string | undefined = undefined;
  @observable billingAccountId: string | undefined = undefined;
  @observable billingAccountVatCountry: string | undefined = undefined;
  @observable billingAccountType: string | undefined = undefined;
  @observable shippingAddressCountry: string | undefined = undefined;
  @observable vatData: DealOrderConfirmationVatQuery['dealOrderConfirmationVat'] | null = null;
  @observable initialValues: OCFormValues = {} as OCFormValues;
  @observable baNotValid = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @action setBANotValid = (baNotValid: boolean) => {
    this.baNotValid = baNotValid;
  };

  @action setBillingAccountId = (billingAccountId: string | undefined) => {
    this.billingAccountId = billingAccountId;
  };

  @action setOrderConfirmationBillingAccount = (ba: OrderConfirmation['billingAccount'] | undefined | null) => {
    this.orderConfirmation = {
      ...this.orderConfirmation,
      billingAccount: ba ?? undefined,
    } as OrderConfirmation;
  };

  @action updateInitialValues = () => {
    if (!this.vatData) return;

    this.initialValues = {
      ...this.initialValues,
      lots: this.initialValues.lots?.map((lot) => ({
        ...lot,
        vatRate: this.vatData?.vatRate || lot.vatRate || 0,
      })),
      services: this.initialValues.services?.map((service) => ({
        ...service,
        vatRate: this.vatData?.vatRate || service.vatRate || 0,
      })),
    };
  };

  @action setVatData = (vatData: DealOrderConfirmationVatQuery['dealOrderConfirmationVat'] | null) => {
    this.vatData = vatData;
  };

  @action setBillingAccountCountry = (billingAccountCountry: string | undefined) => {
    this.billingAccountCountry = billingAccountCountry;
  };

  @action setBillingAccountVatCountry = (billingAccountVatCountry: string | undefined) => {
    this.billingAccountVatCountry = billingAccountVatCountry;
  };

  @action setBillingAccountType = (billingAccountType: string | undefined) => {
    this.billingAccountType = billingAccountType;
  };

  @action setShippingAddressCountry = (shippingAddressCountry: string | undefined) => {
    this.shippingAddressCountry = shippingAddressCountry;
  };

  @action private setDealId = (dealId: number | string): void => {
    this.dealId = Number(dealId);
  };

  @action clearCache = () => {
    this.orderConfirmation = {} as OrderConfirmation;
  };

  @action setIsExist = (value: boolean): void => {
    this.isExist = value;
  };

  @action fetch = async (dealId: number | string) => {
    this.setIsFetching(true);

    try {
      this.setDealId(dealId);
      const response = await api.get(routesApi.dealOrderConfirmation(dealId));
      const payload = response?.data?.payload;

      if (payload && payload.id) this.setIsExist(true);

      this.assignAttributes(payload);

      this.setIsFetching(false);
    } catch (e) {
      console.error(e);
      this.setIsFetching(false);
    }
  };

  @action create = async (values: any) => {
    this.setIsFetching(true);

    const attributes = this.prependAttributes(values);

    try {
      await api.post(routesApi.dealOrderConfirmation(this.dealId), attributes);

      this.clearCache();

      window.location.reload();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);

      this.setIsFetching(false);

      const errors = e?.response?.data?.errors?.source as unknown as Record<string, string> | null;
      const message = e?.response?.data?.errors?.title as unknown as string | null;

      if (isEmpty(errors)) {
        if (message) {
          Notification.Error(message);
        } else {
          Notification.Error('Something went wrong, please reload the page and try again.');
        }

        return;
      }

      if (errors) {
        Object.keys(errors).forEach((key) => {
          Notification.Error(`${key !== 'base' ? `${key}: ` : ''} ${errors[key]}`);
        });
      }
    }
  };

  @action update = async (
    values: Record<string, unknown>,
    skipCreditLimitWarningComment?: string,
  ): Promise<string | undefined> => {
    this.rootStore.pageLoaderStore.enable('Updating OC');

    const defaultAttributes = {
      ...this.prependAttributes(values),
      ...(skipCreditLimitWarningComment ? { skip_credit_limit_warning_comment: skipCreditLimitWarningComment } : {}),
    };

    const attributesWithVat = {
      ...defaultAttributes,
      xometry_vat_number_id: this.vatData?.xomVatNumber?.id,
    };

    const attributes = XOMETRY_VAT_ENABLED ? attributesWithVat : defaultAttributes;

    try {
      const billingAccount = values.billingAccount as { deletedAt?: string; id?: string } | undefined;

      if (billingAccount?.deletedAt) {
        Notification.Error('Error', `You are about to update OC using Deleted BA-${billingAccount.id || ''}`);
        this.rootStore.pageLoaderStore.disable();

        return undefined;
      }

      const response = await api.patch(routesApi.dealOrderConfirmation(this.dealId), attributes);
      const jid = response?.data?.payload?.jid as string | undefined;

      if (!jid) {
        this.rootStore.pageLoaderStore.disable();
      }

      // Return jobId if present for async job status polling
      return jid;
    } catch {
      this.rootStore.pageLoaderStore.disable();
      Notification.Error('Error', 'Something went wrong. Please try again later.');

      return undefined;
    }
  };

  @action updateCurrency = async (currency: string) => {
    this.rootStore.pageLoaderStore.enable('Updating OC');

    try {
      const response = await api.patch(routesApi.orderConfirmationUpdateCurrencyPath(this.dealId), { currency });
      this.assignAttributes(response.data.payload);
    } catch (e: unknown) {
      this.rootStore.pageLoaderStore.disable();

      return e;
    }

    if (this.reloadOnUpdate) {
      window.location.reload();
    }

    return null;
  };

  @action publish = async (
    values: OCFormValues | Record<string, unknown>,
    skipCreditLimitWarningComment?: string,
  ): Promise<string | undefined> => {
    this.rootStore.pageLoaderStore.enable('Updating & Publishing...');

    const defaultAttributes = {
      ...this.prependAttributes(values),
      published_at: dayjs().format('DD.MM.YYYY'),
      ...(skipCreditLimitWarningComment ? { skip_credit_limit_warning_comment: skipCreditLimitWarningComment } : {}),
    };

    const attributesWithVat = {
      ...defaultAttributes,
      xometry_vat_number_id: this.vatData?.xomVatNumber?.id,
    };

    const attributes = XOMETRY_VAT_ENABLED ? attributesWithVat : defaultAttributes;

    try {
      const response = await api.patch(routesApi.dealOrderConfirmation(this.dealId), attributes);
      const jid = response?.data?.payload?.jid as string | undefined;

      if (!jid) {
        this.rootStore.pageLoaderStore.disable();
      }

      // Return jobId if present for async job status polling
      return jid;
    } catch {
      this.rootStore.pageLoaderStore.disable();

      return undefined;
    }
  };

  @action destroy = async (): Promise<void> => {
    this.setIsFetching(true);

    try {
      await api.delete(routesApi.dealOrderConfirmation(this.dealId));

      this.clearCache();

      window.location.reload();

      this.setIsFetching(false);
    } catch (e) {
      this.setIsFetching(false);
      console.error(e);
    }
  };

  @action fetchLots = async () => {
    this.rootStore.pageLoaderStore.enable('Fetching Parts and Lots...');

    try {
      const response = await api.get(routesApi.orderConfirmationPositionsPath(this.dealId));

      const payload = response?.data?.payload;
      const lots = camelizeKeys(payload);

      this.setLots(lots);
      this.rootStore.pageLoaderStore.disable();

      return this.orderConfirmation.lots;
    } catch (e) {
      this.rootStore.pageLoaderStore.disable();
      console.error(e);
    }
  };

  @action fetchComment = async (values: CommentRequest): Promise<string> => {
    const response = await omsGet(routesApi.orderConfirmationCommentPath(this.dealId), decamelizeKeys(values));
    const payload: string = response?.data?.payload;

    return payload || '';
  };

  private prependAttributes = (values: any) => {
    const valuesWithoutBilling = omit(values, ['billingAccount']);

    const lots = values.lots?.reduce((acc: OCLot[], lot: FormLot) => {
      if (lot.partType === PartType.Mold) {
        return [...acc, lot, ...(lot.children || [])];
      }

      return [...acc, lot];
    }, []);

    return decamelizeKeys({
      ...valuesWithoutBilling,
      dealId: this.dealId,
      lots,
      confirmDate: values.confirmDate ? dateFormat(values.confirmDate) : undefined,
      deliveryDate: values.deliveryDate ? dateFormat(values.deliveryDate) : undefined,
      publishedAt: values.publishedAt ? dateFormat(values.publishedAt) : null,
    });
  };

  @action private setIsFetching = (value: boolean): boolean => {
    this.isFetching = value;

    return this.isFetching;
  };

  @action setCreditOverflow = (creditOverflow?: CreditOverflow) => {
    this.creditOverflow = creditOverflow;
  };

  @action setReloadOnUpdate = (reloadOnUpdate: boolean) => {
    this.reloadOnUpdate = reloadOnUpdate;
  };

  @action private setLots = (lots: OCLot[]) => {
    const vat = this.rootStore.dealStore.data.vat || 0;

    const lotsWithKeys = sortBy(
      camelizeKeys(lots).map((lot: OCLot) => ({
        ...lot,
        _key: uniqID(),
        vatRate: this.vatData?.vatRate || lot.vatRate || vat || 0,
      })),
      (lot: OCLot) => lot.partPosition,
    );

    this.orderConfirmation = {
      ...this.orderConfirmation,
      lots: lotsWithKeys,
    };
  };

  @action private assignAttributes = (oc: any) => {
    const orderConfirmation = camelizeKeys(oc);

    try {
      this.orderConfirmation = {
        ...camelizeKeys(orderConfirmation),
        lots: this.preparePositions(orderConfirmation.lots),
        services: this.preparePositions(orderConfirmation.services),
        shippingAddress: this.convertShippingAddress(orderConfirmation.shippingAddress),
      };

      this.billingAccountCountry = orderConfirmation.billingAccount?.country;
      this.billingAccountId = orderConfirmation.billingAccount?.id;
      this.billingAccountVatCountry = orderConfirmation.billingAccount?.vatRegistrationCountry;
      this.billingAccountType = orderConfirmation.billingAccount?.accountType;
      this.shippingAddressCountry = orderConfirmation.shippingAddress?.country;

      this.initialValues = getInitialValues(this.orderConfirmation);
    } catch (e) {
      console.error(e);
    }
  };

  @computed get customerAddress(): IAddress | undefined {
    const ocShippingAddress = this.orderConfirmation.shippingAddress;

    if (!ocShippingAddress) return undefined;

    return {
      id: ocShippingAddress.id ? String(ocShippingAddress.id) : undefined,
      address: ocShippingAddress.address || '',
      city: ocShippingAddress.city || '',
      zip: ocShippingAddress.zip || '',
      region: ocShippingAddress.region || '',
      country: ocShippingAddress.country || '',
      company: ocShippingAddress.companyName || '',
      name: ocShippingAddress.name || '',
      phone: ocShippingAddress.phone || '',
      comment: ocShippingAddress.comment || '',
      internalComment: ocShippingAddress.internalComment || '',
    };
  }

  get isProdIntercompanyRole(): boolean {
    return this.orderConfirmation.intercompanyRole === 'prod';
  }

  private preparePositions = <T extends { vatRate?: number }>(positions: T[]): T[] =>
    camelizeKeys(positions).map((position: T) => {
      const defaultPosition = {
        ...position,
        _key: uniqID(),
      };

      const positionWithVat = {
        ...defaultPosition,
        vatRate: this.vatData?.vatRate || position?.vatRate || 0,
      };

      return XOMETRY_VAT_ENABLED ? positionWithVat : defaultPosition;
    });

  private convertShippingAddress = (shippingAddress: any) => {
    if (!shippingAddress) return shippingAddress;

    const { phone, name, contactPhone, contactName, ...rest } = shippingAddress;

    return {
      ...rest,
      contactPhone: contactPhone || phone,
      contactName: contactName || name,
    };
  };
}

export default OrderConfirmationStore;
